import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useMembership } from '../contexts/MembershipContext';
import { Button } from './ui/button';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuSeparator,
    DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { cn } from '../lib/utils';
import { 
    Stethoscope,
    LayoutDashboard,
    BookOpen,
    BarChart3,
    XCircle,
    RefreshCw,
    LogOut,
    User,
    Menu,
    GraduationCap,
    Shield,
    CalendarDays,
    Sparkles,
    Moon,
    Sun,
    Ticket,
    Library,
    ClipboardList,
    Activity,
    ChevronDown,
    Download,
    CreditCard,
    RotateCcw
} from 'lucide-react';
import { Toaster } from './ui/sonner';
import { MembershipBadge, UpgradeModal } from './MembershipComponents';
import { AnkiExportModal } from './AnkiExportModal';
import { analyticsAPI } from '../lib/api';
import { toast } from 'sonner';

const navItems = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/study-planner', label: 'Study Planner', icon: CalendarDays, highlight: true },
    { href: '/usmle-steps', label: 'USMLE Steps', icon: ClipboardList },
    { href: '/library', label: 'Library', icon: Library },
    { href: '/practice', label: 'Practice', icon: BookOpen },
    // CBSE + Shelf combined dropdown — see CbseShelfDropdown below
    { href: '/analytics', label: 'Analytics', icon: BarChart3 },
    // Missed + Review combined dropdown — see MissedReviewDropdown below
];

// Combined CBSE / Shelf dropdown item
function CbseShelfDropdown({ isDark, location }) {
    const navigate = useNavigate();
    const isActive = location.pathname === '/cbse' || location.pathname === '/shelf-exam';
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors outline-none",
                        isActive
                            ? isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-100 text-indigo-700"
                            : isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                    )}
                    data-testid="nav-cbse-shelf-dropdown"
                >
                    <GraduationCap className="h-4 w-4" />
                    CBSE / Shelf
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className={cn("w-44", isDark && "bg-slate-800 border-slate-700")}>
                <DropdownMenuItem
                    onClick={() => navigate('/cbse')}
                    className={cn(
                        "cursor-pointer gap-2",
                        location.pathname === '/cbse' && (isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-50 text-indigo-700"),
                        isDark && "text-slate-200 focus:bg-slate-700"
                    )}
                    data-testid="nav-cbse-link"
                >
                    <GraduationCap className="h-4 w-4" />
                    CBSE Exam
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => navigate('/shelf-exam')}
                    className={cn(
                        "cursor-pointer gap-2",
                        location.pathname === '/shelf-exam' && (isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-50 text-indigo-700"),
                        isDark && "text-slate-200 focus:bg-slate-700"
                    )}
                    data-testid="nav-shelf-exam"
                >
                    <Activity className="h-4 w-4" />
                    Shelf Exam
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// Combined Missed / Review dropdown item
function MissedReviewDropdown({ isDark, location }) {
    const navigate = useNavigate();
    const isActive = location.pathname === '/missed' || location.pathname === '/review';
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors outline-none",
                        isActive
                            ? isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-100 text-indigo-700"
                            : isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                    )}
                    data-testid="nav-missed-review-dropdown"
                >
                    <XCircle className="h-4 w-4" />
                    Missed
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className={cn("w-48", isDark && "bg-slate-800 border-slate-700")}>
                <DropdownMenuItem
                    onClick={() => navigate('/missed')}
                    className={cn(
                        "cursor-pointer gap-2",
                        location.pathname === '/missed' && (isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-50 text-indigo-700"),
                        isDark && "text-slate-200 focus:bg-slate-700"
                    )}
                    data-testid="nav-missed-questions-link"
                >
                    <XCircle className="h-4 w-4" />
                    Missed Questions
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => navigate('/review')}
                    className={cn(
                        "cursor-pointer gap-2",
                        location.pathname === '/review' && (isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-50 text-indigo-700"),
                        isDark && "text-slate-200 focus:bg-slate-700"
                    )}
                    data-testid="nav-review-link"
                >
                    <RefreshCw className="h-4 w-4" />
                    Review
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const { canAccess, setShowUpgradeModal } = useMembership();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const [showAnkiModal, setShowAnkiModal] = useState(false);

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };
    
    // Add professor/admin links based on role
    let allNavItems = [...navItems];
    if (user?.is_professor || user?.is_admin) {
        allNavItems.push({ href: '/professor', label: 'Professor', icon: GraduationCap });
    }
    if (user?.is_admin) {
        allNavItems.push({ href: '/admin', label: 'Admin', icon: Shield });
    }

    return (
        <div className={cn("min-h-screen transition-colors duration-200", isDark ? "bg-slate-900" : "bg-slate-50")}>
            {/* Navigation */}
            <nav className={cn(
                "fixed top-0 left-0 right-0 z-50 backdrop-blur-lg border-b transition-colors duration-200",
                isDark ? "bg-slate-900/80 border-slate-700" : "bg-white/80 border-slate-200"
            )}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <Link to="/dashboard" className="flex items-center gap-2">
                            <Stethoscope className={cn("h-8 w-8", isDark ? "text-indigo-400" : "text-indigo-600")} />
                            <span className={cn("text-xl font-bold hidden sm:block", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: 'Manrope' }}>
                                MedPrep
                            </span>
                        </Link>

                        {/* Desktop Nav */}
                        <div className="hidden md:flex items-center gap-1">
                            {allNavItems.map((item) => (
                                <React.Fragment key={item.href}>
                                    <Link
                                        to={item.href}
                                        className={cn(
                                            "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                                            location.pathname === item.href
                                                ? isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-100 text-indigo-700"
                                                : isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100",
                                            item.label === 'Admin' && (isDark ? "bg-indigo-900/30 text-indigo-400" : "bg-indigo-50 text-indigo-600"),
                                            item.highlight && location.pathname !== item.href && (isDark ? "bg-gradient-to-r from-amber-900/30 to-orange-900/30 text-amber-400 border border-amber-700 hover:from-amber-900/50 hover:to-orange-900/50" : "bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-200 hover:from-amber-100 hover:to-orange-100")
                                        )}
                                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                                    >
                                        {item.highlight && location.pathname !== item.href ? (
                                            <Sparkles className={cn("h-4 w-4", isDark ? "text-amber-400" : "text-amber-500")} />
                                        ) : (
                                            <item.icon className="h-4 w-4" />
                                        )}
                                        {item.label}
                                    </Link>
                                    {/* Insert CBSE / Shelf dropdown right after USMLE Steps */}
                                    {item.href === '/usmle-steps' && (
                                        <CbseShelfDropdown isDark={isDark} location={location} />
                                    )}
                                    {/* Insert Missed / Review dropdown right after Analytics */}
                                    {item.href === '/analytics' && (
                                        <MissedReviewDropdown isDark={isDark} location={location} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* User Menu */}
                        <div className="flex items-center gap-4">
                            {/* Membership Badge */}
                            <MembershipBadge />

                            {/* Mobile Menu */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild className="md:hidden">
                                    <Button variant="ghost" size="icon">
                                        <Menu className="h-5 w-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    {allNavItems.map((item) => (
                                        <DropdownMenuItem 
                                            key={item.href}
                                            onClick={() => navigate(item.href)}
                                            className={cn(
                                                location.pathname === item.href && "bg-indigo-50 text-indigo-700",
                                                item.highlight && "text-amber-700 font-medium"
                                            )}
                                            data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                                        >
                                            {item.highlight ? (
                                                <Sparkles className="mr-2 h-4 w-4 text-amber-500" />
                                            ) : (
                                                <item.icon className="mr-2 h-4 w-4" />
                                            )}
                                            {item.label}
                                        </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => navigate('/cbse')}
                                        className={cn(location.pathname === '/cbse' && "bg-indigo-50 text-indigo-700")}
                                        data-testid="mobile-nav-cbse"
                                    >
                                        <GraduationCap className="mr-2 h-4 w-4" />
                                        CBSE Exam
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => navigate('/shelf-exam')}
                                        className={cn(location.pathname === '/shelf-exam' && "bg-indigo-50 text-indigo-700")}
                                        data-testid="mobile-nav-shelf-exam"
                                    >
                                        <Activity className="mr-2 h-4 w-4" />
                                        Shelf Exam
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => navigate('/missed')}
                                        className={cn(location.pathname === '/missed' && "bg-indigo-50 text-indigo-700")}
                                        data-testid="mobile-nav-missed"
                                    >
                                        <XCircle className="mr-2 h-4 w-4" />
                                        Missed Questions
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => navigate('/review')}
                                        className={cn(location.pathname === '/review' && "bg-indigo-50 text-indigo-700")}
                                        data-testid="mobile-nav-review"
                                    >
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Review
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* User Dropdown */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className={cn("flex items-center gap-2", isDark && "hover:bg-slate-800")} data-testid="user-menu-btn">
                                        <Avatar className="h-8 w-8">
                                            <AvatarFallback className={cn(isDark ? "bg-indigo-900 text-indigo-300" : "bg-indigo-100 text-indigo-700", "text-sm")}>
                                                {getInitials(user?.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className={cn("hidden sm:block text-sm font-medium", isDark ? "text-slate-200" : "text-slate-700")}>
                                            {user?.name?.split(' ')[0]}
                                        </span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className={cn("w-56", isDark && "bg-slate-800 border-slate-700")}>
                                    <div className="px-3 py-2">
                                        <p className={cn("font-medium", isDark ? "text-white" : "text-slate-900")}>{user?.name}</p>
                                        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>{user?.email}</p>
                                    </div>
                                    <DropdownMenuSeparator className={isDark ? "bg-slate-700" : ""} />
                                    <DropdownMenuItem 
                                        onClick={toggleTheme} 
                                        className={cn("cursor-pointer", isDark ? "text-slate-200 focus:bg-slate-700" : "")}
                                        data-testid="theme-toggle-btn"
                                    >
                                        {isDark ? (
                                            <>
                                                <Sun className="mr-2 h-4 w-4 text-amber-400" />
                                                Light Mode
                                            </>
                                        ) : (
                                            <>
                                                <Moon className="mr-2 h-4 w-4 text-slate-600" />
                                                Dark Mode
                                            </>
                                        )}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                        onClick={() => setShowUpgradeModal(true)} 
                                        className={cn("cursor-pointer", isDark ? "text-slate-200 focus:bg-slate-700" : "")}
                                    >
                                        <Ticket className="mr-2 h-4 w-4 text-indigo-500" />
                                        Redeem Access Code
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                        onClick={() => navigate('/payment')} 
                                        className={cn("cursor-pointer", isDark ? "text-slate-200 focus:bg-slate-700" : "")}
                                        data-testid="manage-subscription-menu-item"
                                    >
                                        <CreditCard className="mr-2 h-4 w-4 text-emerald-500" />
                                        Manage Subscription
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                        onClick={() => setShowAnkiModal(true)} 
                                        className={cn("cursor-pointer", isDark ? "text-slate-200 focus:bg-slate-700" : "")}
                                        data-testid="anki-export-menu-item"
                                    >
                                        <Download className="mr-2 h-4 w-4 text-emerald-500" />
                                        Export to Anki
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                        onClick={async () => {
                                            if (!window.confirm('Are you sure you want to reset all your statistics? This will clear all your practice history, exam results, and progress. This cannot be undone.')) return;
                                            try {
                                                await analyticsAPI.resetStats();
                                                toast.success('All statistics have been reset');
                                            } catch {
                                                toast.error('Failed to reset statistics');
                                            }
                                        }} 
                                        className={cn("cursor-pointer", isDark ? "text-slate-200 focus:bg-slate-700" : "")}
                                        data-testid="reset-stats-menu-item"
                                    >
                                        <RotateCcw className="mr-2 h-4 w-4 text-amber-500" />
                                        Reset Stats
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className={isDark ? "bg-slate-700" : ""} />
                                    <DropdownMenuItem onClick={handleLogout} className={cn("text-red-600 cursor-pointer", isDark && "text-red-400 focus:bg-slate-700")}>
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Sign Out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {children}
                </div>
            </main>

            {/* Upgrade Modal */}
            <UpgradeModal />

            {/* Anki Export Modal */}
            <AnkiExportModal open={showAnkiModal} onOpenChange={setShowAnkiModal} />

            {/* Toast */}
            <Toaster position="top-right" richColors theme={isDark ? "dark" : "light"} />
        </div>
    );
}
